// Base-rate engine for the Scan "Reading Lab" panels (D-096/D-097). Pure and testable:
// given a card's setup and the candle history for each timeframe it uses, it REPLAYS
// history — at each past bar it asks "would this setup have flagged here?" using only
// data available at that bar (info-time honest: readings are computed as-of, never
// with look-ahead) — and, for every firing, records what price did over the next
// `horizon` bars. The output is a DESCRIPTIVE base rate, never a prediction, and it is
// gated on sample size (standing rule #6: analytics need a threshold before they speak).
//
// Everything below is built on ONE replay pass (`replay`) that yields a record per
// firing; base rate, MFE/MAE, and the regime split all aggregate those records so the
// numbers can never disagree with each other.
//
// Multi-timeframe: the setup is replayed on ONE evaluation clock — the most common
// timeframe in the stack, ties broken to the coarsest (longest history). Readings on
// other timeframes are sampled as-of each eval bar's close. Forward returns are measured
// on the eval timeframe.

import type { OHLCPoint } from "./market-data.js";
import { evalReading, verdict, breakoutVerdict, filtersPass, gatesPass, relativeVolume, volatilityPercentile, TIMEFRAMES, SHORT, READING_LABEL, type Card, type Column, type Verdict } from "./confluence.js";

/** Below this many firings we refuse to quote a base rate (rule #6). */
export const MIN_FIRINGS = 20;
/** |efficiency − 0.5| at or above this counts the bar as "trending", else "ranging". */
const TREND_CUT = 0.15;

export interface BaseRate {
  n: number;                  // firings counted over the replay
  evalTf: string;             // timeframe the setup was replayed on
  horizon: number;            // forward bars measured
  hitRate: number | null;     // fraction of firings that moved the signalled way
  median: number | null;      // median oriented forward return (+ = went as signalled)
  p10: number | null;         // worst-decile oriented return (the bad tail)
  p90: number | null;         // best-decile oriented return
  avg: number | null;         // mean oriented return
  avgMFE: number | null;      // avg max FAVOURABLE excursion before the horizon (≥ 0)
  avgMAE: number | null;      // avg max ADVERSE excursion before the horizon (≤ 0)
  samples: number[];          // every oriented forward return (for the histogram)
  inHit: number | null;       // hit rate over the FIRST half of history
  outHit: number | null;      // hit rate over the SECOND half (out-of-sample-ish)
  enough: boolean;            // n >= MIN_FIRINGS
  limitingTf: string | null;  // the stack's shortest-history timeframe (explains a small n)
}

type Setup = Pick<Card, "cols" | "rule" | "k" | "os" | "ob" | "filters" | "engine" | "breakout" | "mode">;

interface Firing {
  i: number;          // eval-series index of the firing bar
  dir: 1 | -1;        // +1 buy (oversold), −1 sell (overbought)
  oriented: number;   // forward return oriented so + = went the signalled way
  mfe: number;        // max favourable excursion over the window (≥ 0)
  mae: number;        // max adverse excursion over the window (≤ 0)
  eff: number | null; // trend efficiency as-of the firing bar (for the regime split)
}
interface Replay { evalTf: string; evalSeries: OHLCPoint[]; firings: Firing[]; limitingTf: string | null; }

const coarser = (a: string, b: string): string => (TIMEFRAMES.indexOf(a) >= TIMEFRAMES.indexOf(b) ? a : b);

/** The timeframe to replay on: the most common among the columns, ties → coarsest. */
export function evalTimeframe(cols: Column[]): string {
  const count = new Map<string, number>();
  for (const c of cols) count.set(c.tf, (count.get(c.tf) ?? 0) + 1);
  let best = cols[0]?.tf ?? "1D";
  let bestN = 0;
  for (const [tf, n] of count) {
    if (n > bestN || (n === bestN && coarser(tf, best) === tf)) { best = tf; bestN = n; }
  }
  return best;
}

const quantile = (sorted: number[], q: number): number => {
  if (!sorted.length) return NaN;
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.round(q * (sorted.length - 1))));
  return sorted[idx]!;
};
const mean = (a: number[]): number | null => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);

/** One replay pass over history → a record per firing (info-time honest). */
function replay(setup: Setup, seriesByTf: Map<string, OHLCPoint[]>, horizon: number): Replay {
  const bo = setup.engine === "breakout" ? setup.breakout : undefined;
  // The breakout engine replays on its channel timeframe; the confluence engine on the
  // most-common column timeframe (D-115).
  const evalTf = bo ? bo.tf : evalTimeframe(setup.cols);
  const evalSeries = seriesByTf.get(evalTf) ?? [];
  let limitingTf: string | null = null;
  let fewest = Infinity;
  const structTfs = bo ? [bo.tf, ...(bo.confirm ? [bo.confirm.tf] : [])] : setup.cols.map((c) => c.tf);
  for (const tf of new Set(structTfs)) {
    const len = seriesByTf.get(tf)?.length ?? 0;
    if (len < fewest) { fewest = len; limitingTf = tf; }
  }
  const firings: Firing[] = [];
  if (evalSeries.length < horizon + 5) return { evalTf, evalSeries, firings, limitingTf };

  const colSeries = setup.cols.map((c) => seriesByTf.get(c.tf) ?? []);
  const ptr = setup.cols.map(() => -1);
  // Reading gates (D-115) run on their own timeframes, sampled as-of each eval bar just
  // like out-of-clock columns — so the base rate only counts firings where the context
  // (e.g. Dip's trend-up gate) actually held, keeping the "After it fires" numbers honest.
  const gates = setup.filters?.gates ?? [];
  const gateSeries = gates.map((g) => seriesByTf.get(g.tf) ?? []);
  const gptr = gates.map(() => -1);
  // Breakout confirm reading: its own series, sampled as-of (D-115).
  const confirmSeries = bo?.confirm ? seriesByTf.get(bo.confirm.tf) ?? [] : [];
  let cptr = -1;
  const last = evalSeries.length - 1 - horizon;
  for (let i = 0; i <= last; i++) {
    const t = evalSeries[i]!.date.getTime();
    let v: Verdict;
    if (bo) {
      let confirmVal: number | null = null;
      if (bo.confirm) {
        if (bo.confirm.tf === evalTf) confirmVal = evalReading(bo.confirm.reading, evalSeries.slice(0, i + 1)).v;
        else {
          while (cptr + 1 < confirmSeries.length && confirmSeries[cptr + 1]!.date.getTime() <= t) cptr++;
          confirmVal = cptr >= 0 ? evalReading(bo.confirm.reading, confirmSeries.slice(0, cptr + 1)).v : null;
        }
      }
      v = breakoutVerdict(evalSeries.slice(0, i + 1), bo, confirmVal);
    } else {
      const vals: Array<number | null> = [];
      for (let c = 0; c < setup.cols.length; c++) {
        const col = setup.cols[c]!;
        let slice: OHLCPoint[];
        if (col.tf === evalTf) slice = evalSeries.slice(0, i + 1);
        else {
          const s = colSeries[c]!;
          while (ptr[c]! + 1 < s.length && s[ptr[c]! + 1]!.date.getTime() <= t) ptr[c]!++;
          slice = ptr[c]! >= 0 ? s.slice(0, ptr[c]! + 1) : [];
        }
        vals.push(evalReading(col.reading, slice, { period: col.period, lookback: col.lookback }).v);
      }
      v = verdict(vals, setup as Card);
      if (v && setup.filters) {
        const upTo = evalSeries.slice(0, i + 1);
        if (!filtersPass(setup.filters, { relVol: relativeVolume(upTo), volatilityPct: volatilityPercentile(upTo) })) v = null;
        else if (gates.length) {
          const gvals = gates.map((g, gi) => {
            const s = gateSeries[gi]!;
            if (g.tf === evalTf) return evalReading(g.reading, evalSeries.slice(0, i + 1), { period: g.period, lookback: g.lookback }).v;
            while (gptr[gi]! + 1 < s.length && s[gptr[gi]! + 1]!.date.getTime() <= t) gptr[gi]!++;
            return gptr[gi]! >= 0 ? evalReading(g.reading, s.slice(0, gptr[gi]! + 1), { period: g.period, lookback: g.lookback }).v : null;
          });
          if (!gatesPass(gates, gvals)) v = null;
        }
      }
    }
    if (!v) continue;
    const dir: 1 | -1 = v === "buy" ? 1 : -1;
    const entry = evalSeries[i]!.close;
    const fwd = evalSeries[i + horizon]!.close / entry - 1;
    // Max favourable / adverse excursion over the forward window, using bar highs/lows,
    // oriented by the signal's direction (favourable = the way it signalled).
    let mfe = 0, mae = 0;
    for (let j = i + 1; j <= i + horizon; j++) {
      const hi = evalSeries[j]!.high, lo = evalSeries[j]!.low;
      const fav = dir === 1 ? (hi - entry) / entry : (entry - lo) / entry;
      const adv = dir === 1 ? (lo - entry) / entry : (entry - hi) / entry;
      if (fav > mfe) mfe = fav;
      if (adv < mae) mae = adv;
    }
    firings.push({ i, dir, oriented: dir * fwd, mfe, mae, eff: evalReading("eff", evalSeries.slice(0, i + 1)).v });
  }
  return { evalTf, evalSeries, firings, limitingTf };
}

/** Aggregate a replay into a base rate. */
export function baseRate(setup: Setup, seriesByTf: Map<string, OHLCPoint[]>, horizon: number): BaseRate {
  const { evalTf, evalSeries, firings, limitingTf } = replay(setup, seriesByTf, horizon);
  const empty = (): BaseRate => ({ n: 0, evalTf, horizon, hitRate: null, median: null, p10: null, p90: null, avg: null, avgMFE: null, avgMAE: null, samples: [], inHit: null, outHit: null, enough: false, limitingTf });
  const n = firings.length;
  if (!n) return empty();
  const oriented = firings.map((f) => f.oriented);
  const sorted = [...oriented].sort((a, b) => a - b);
  const hit = oriented.filter((x) => x > 0).length / n;
  const mid = evalSeries.length / 2;
  const first = firings.filter((f) => f.i < mid).map((f) => f.oriented);
  const second = firings.filter((f) => f.i >= mid).map((f) => f.oriented);
  const rate = (a: number[]): number | null => (a.length ? a.filter((x) => x > 0).length / a.length : null);
  return {
    n, evalTf, horizon,
    hitRate: hit,
    median: quantile(sorted, 0.5),
    p10: quantile(sorted, 0.1),
    p90: quantile(sorted, 0.9),
    avg: mean(oriented),
    avgMFE: mean(firings.map((f) => f.mfe)),
    avgMAE: mean(firings.map((f) => f.mae)),
    samples: oriented,
    inHit: rate(first),
    outHit: rate(second),
    enough: n >= MIN_FIRINGS,
    limitingTf,
  };
}

export interface SubRate { n: number; hitRate: number | null; median: number | null; enough: boolean; }
/** Split the firings by the market regime AT each firing (trend efficiency): does the
 * edge depend on whether the market was trending or ranging? */
export function regimeSplit(setup: Setup, seriesByTf: Map<string, OHLCPoint[]>, horizon: number): { trending: SubRate; ranging: SubRate } {
  const { firings } = replay(setup, seriesByTf, horizon);
  const sub = (fs: Firing[]): SubRate => {
    const os = fs.map((f) => f.oriented).sort((a, b) => a - b);
    return { n: fs.length, hitRate: fs.length ? fs.filter((f) => f.oriented > 0).length / fs.length : null, median: fs.length ? quantile(os, 0.5) : null, enough: fs.length >= MIN_FIRINGS };
  };
  const known = firings.filter((f) => f.eff != null);
  return {
    trending: sub(known.filter((f) => Math.abs(f.eff! - 0.5) >= TREND_CUT)),
    ranging: sub(known.filter((f) => Math.abs(f.eff! - 0.5) < TREND_CUT)),
  };
}

/** The eval-series closes + which bars fired and whether the forward move was favourable
 * — for the "firings on price" mini chart. */
export function firingMarks(setup: Setup, seriesByTf: Map<string, OHLCPoint[]>, horizon: number): { closes: number[]; marks: Array<{ i: number; good: boolean }> } {
  const { evalSeries, firings } = replay(setup, seriesByTf, horizon);
  return { closes: evalSeries.map((c) => c.close), marks: firings.map((f) => ({ i: f.i, good: f.oriented > 0 })) };
}

/** A cumulative "strategy equity" path over the eval series for the Confirm chart:
 * starts at 0 and steps by each firing's oriented forward return at its bar (an
 * if-you-took-every-signal P&L path — real, not illustrative). Also returns the
 * buy-&-hold price path and the in/out-of-sample split index (mid-history). */
export function equityCurve(setup: Setup, seriesByTf: Map<string, OHLCPoint[]>, horizon: number): { curve: number[]; hold: number[]; split: number } {
  const { evalSeries, firings } = replay(setup, seriesByTf, horizon);
  const n = evalSeries.length;
  const stepAt = new Map<number, number>();
  for (const f of firings) stepAt.set(f.i, (stepAt.get(f.i) ?? 0) + f.oriented);
  const curve: number[] = [];
  let cum = 0;
  for (let i = 0; i < n; i++) { cum += stepAt.get(i) ?? 0; curve.push(cum); }
  const base0 = evalSeries[0]?.close ?? 0;
  const hold = evalSeries.map((c) => (base0 ? (c.close - base0) / base0 : 0));
  return { curve, hold, split: Math.floor(n / 2) };
}

/** Buy-&-hold price path for a raw candle series (cumulative return from the first
 * bar) — for a benchmark line (e.g. the S&P 500) on the Confirm chart. */
export function holdCurve(series: OHLCPoint[]): number[] {
  const base0 = series[0]?.close ?? 0;
  return series.map((c) => (base0 ? (c.close - base0) / base0 : 0));
}

export interface AttributionRow { label: string; kind: "full" | "single" | "drop"; n: number; hitRate: number | null; median: number | null; enough: boolean; }

/** "Is the agreement earning its keep?" — the full stack vs each reading alone, and vs
 * the stack with one reading removed (leave-one-out). */
export function attribution(setup: Setup, seriesByTf: Map<string, OHLCPoint[]>, horizon: number): AttributionRow[] {
  const row = (label: string, kind: AttributionRow["kind"], s: Setup): AttributionRow => {
    const r = baseRate(s, seriesByTf, horizon);
    return { label, kind, n: r.n, hitRate: r.hitRate, median: r.median, enough: r.enough };
  };
  const rows: AttributionRow[] = [row("Full stack", "full", setup)];
  for (const col of setup.cols) rows.push(row(SHORT[col.reading] + " " + col.tf, "single", { ...setup, cols: [col], rule: "all", k: 1 }));
  if (setup.cols.length > 1) {
    setup.cols.forEach((col, idx) => {
      const rest = setup.cols.filter((_, j) => j !== idx);
      const need = setup.rule === "k" ? Math.min(setup.k, rest.length) : rest.length;
      rows.push(row("– " + READING_LABEL[col.reading] + " " + col.tf, "drop", { ...setup, cols: rest, k: need }));
    });
  }
  return rows;
}

// ---- cross-sectional: does ranking the watchlist by a measure pick winners? --------

/** Fewest watchlist names before a cross-sectional stat is worth quoting. */
export const MIN_FIELD = 5;

const crossMeasure = (candles: OHLCPoint[], closes: number[], i: number, measure: string, lb: number): number | null => {
  if (measure === "ret") return i >= lb ? closes[i]! / closes[i - lb]! - 1 : null;
  if (measure === "mom") { const m = Math.min(lb * 3, 60); return i >= m ? closes[i]! / closes[i - m]! - 1 : null; }
  if (measure === "z") {
    if (i < lb) return null;
    const w = closes.slice(i - lb + 1, i + 1);
    const mean = w.reduce((a, b) => a + b, 0) / w.length;
    const sd = Math.sqrt(w.reduce((a, b) => a + (b - mean) ** 2, 0) / w.length);
    return sd ? (closes[i]! - mean) / sd : 0;
  }
  // "rng": Donchian position in the last-lb high/low channel.
  if (i < lb) return null;
  const w = candles.slice(i - lb + 1, i + 1);
  const hi = Math.max(...w.map((c) => c.high)), lo = Math.min(...w.map((c) => c.low));
  return hi > lo ? (candles[i]!.close - lo) / (hi - lo) : 0.5;
};

export interface CrossEdge {
  measure: string;
  n: number;              // dates evaluated
  meanSpread: number | null; // avg (top-third forward return − bottom-third forward return)
  hitRate: number | null;    // fraction of dates the top third beat the bottom third
  inHit: number | null;
  outHit: number | null;
  fieldSize: number;      // names with usable history
  enough: boolean;
}

/** For each past date, rank the field by `measure`, then compare the forward return of the
 * top third vs the bottom third. A positive spread means the ranking picked winners. Aligns
 * series from the most-recent end by index (a validation-grade approximation that assumes
 * shared trading days). Gated on field size (MIN_FIELD) and date count (MIN_FIRINGS). */
export function crossEdge(syms: string[], seriesByTf: Map<string, OHLCPoint[]>, measure: string, lookback: number, horizon: number): CrossEdge {
  const lb = Math.max(2, Math.round(lookback));
  const arrs = [...new Set(syms)].map((s) => seriesByTf.get(s)).filter((c): c is OHLCPoint[] => !!c && c.length > lb + horizon + 2);
  const fieldSize = arrs.length;
  const empty = (): CrossEdge => ({ measure, n: 0, meanSpread: null, hitRate: null, inHit: null, outHit: null, fieldSize, enough: false });
  if (fieldSize < MIN_FIELD) return empty();
  const L = Math.min(...arrs.map((a) => a.length));
  const win = arrs.map((a) => a.slice(-L));
  const closes = win.map((a) => a.map((c) => c.close));
  const third = Math.max(1, Math.floor(fieldSize / 3));
  const spreads: number[] = [];
  for (let i = lb; i <= L - 1 - horizon; i++) {
    const vals: Array<{ v: number; fwd: number }> = [];
    for (let k = 0; k < fieldSize; k++) {
      const v = crossMeasure(win[k]!, closes[k]!, i, measure, lb);
      if (v == null) continue;
      vals.push({ v, fwd: closes[k]![i + horizon]! / closes[k]![i]! - 1 });
    }
    if (vals.length < MIN_FIELD) continue;
    vals.sort((a, b) => b.v - a.v);
    const top = vals.slice(0, third), bot = vals.slice(-third);
    spreads.push(top.reduce((a, b) => a + b.fwd, 0) / top.length - bot.reduce((a, b) => a + b.fwd, 0) / bot.length);
  }
  const n = spreads.length;
  if (!n) return empty();
  const half = Math.floor(n / 2);
  const rate = (a: number[]): number | null => (a.length ? a.filter((x) => x > 0).length / a.length : null);
  return { measure, n, meanSpread: spreads.reduce((a, b) => a + b, 0) / n, hitRate: rate(spreads), inHit: rate(spreads.slice(0, half)), outHit: rate(spreads.slice(half)), fieldSize, enough: n >= MIN_FIRINGS };
}

// ---- intermarket: do stretched spreads on correlated pairs revert? -----------------

const retsArr = (closes: number[]): number[] => { const r: number[] = []; for (let i = 1; i < closes.length; i++) r.push(closes[i]! / closes[i - 1]! - 1); return r; };

export interface InterPair {
  partner: string;
  corr: number;               // correlation to the anchor over the recent window
  corrOld: number | null;     // correlation over an older window (for stability)
  stable: boolean;            // same-sign & meaningfully correlated in both halves
  revertRate: number | null;  // fraction of ±z spread stretches that reverted
  revertMedian: number | null;
  n: number;                  // stretch count
  enough: boolean;
}

/** For the anchor's most-correlated partners: (1) is the correlation stable across time,
 * and (2) when the pair's spread (log A − log B) stretched to ±zThreshold, did it revert
 * over the next `horizon` bars — the classic pairs trade. Aligns from the recent end. */
export function intermarketPairs(anchor: string, syms: string[], seriesByTf: Map<string, OHLCPoint[]>, window: number, zThreshold: number, horizon: number, maxNames: number): { pairs: InterPair[]; fieldSize: number } {
  const w = Math.max(5, Math.round(window));
  const aAll = seriesByTf.get(anchor);
  const others = [...new Set(syms)].filter((s) => s !== anchor).map((s) => ({ s, c: seriesByTf.get(s) })).filter((o): o is { s: string; c: OHLCPoint[] } => !!o.c && o.c.length > w + horizon + 3);
  const fieldSize = others.length;
  if (!aAll || aAll.length < w + horizon + 3 || fieldSize < 1) return { pairs: [], fieldSize };
  const L = Math.min(aAll.length, ...others.map((o) => o.c.length));
  const aClose = aAll.slice(-L).map((c) => c.close);
  const aRet = retsArr(aClose);
  const scored = others.map((o) => { const bClose = o.c.slice(-L).map((c) => c.close); return { s: o.s, bClose, bRet: retsArr(bClose), corr: pearson(aRet.slice(-w), retsArr(o.c.slice(-L).map((c) => c.close)).slice(-w)) ?? 0 }; });
  scored.sort((x, y) => Math.abs(y.corr) - Math.abs(x.corr));
  const pairs: InterPair[] = scored.slice(0, Math.max(1, Math.round(maxNames))).map(({ s, bClose, bRet, corr }) => {
    const mid = Math.floor(aRet.length / 2);
    const corrOld = mid > w ? pearson(aRet.slice(mid - w, mid), bRet.slice(mid - w, mid)) : null;
    const stable = corrOld != null && Math.sign(corrOld) === Math.sign(corr) && Math.abs(corrOld) >= 0.3 && Math.abs(corr) >= 0.3;
    const spread = aClose.map((v, i) => Math.log(v) - Math.log(bClose[i]!));
    const oriented: number[] = [];
    for (let i = w - 1; i <= spread.length - 1 - horizon; i++) {
      const win = spread.slice(i - w + 1, i + 1);
      const m = win.reduce((a, b) => a + b, 0) / win.length;
      const sd = Math.sqrt(win.reduce((a, b) => a + (b - m) ** 2, 0) / win.length);
      if (!sd) continue;
      const z = (spread[i]! - m) / sd;
      if (Math.abs(z) < zThreshold) continue;
      oriented.push(-Math.sign(z) * (spread[i + horizon]! - spread[i]!));
    }
    const n = oriented.length;
    const sorted = [...oriented].sort((a, b) => a - b);
    return { partner: s, corr, corrOld, stable, revertRate: n ? oriented.filter((x) => x > 0).length / n : null, revertMedian: n ? sorted[Math.floor(n / 2)]! : null, n, enough: n >= MIN_FIRINGS };
  });
  return { pairs, fieldSize };
}

const pearson = (a: number[], b: number[]): number | null => {
  const n = a.length;
  if (n < 10) return null;
  const ma = a.reduce((x, y) => x + y, 0) / n, mb = b.reduce((x, y) => x + y, 0) / n;
  let num = 0, da = 0, db = 0;
  for (let i = 0; i < n; i++) { num += (a[i]! - ma) * (b[i]! - mb); da += (a[i]! - ma) ** 2; db += (b[i]! - mb) ** 2; }
  return da && db ? num / Math.sqrt(da * db) : null;
};

export interface Redundancy { a: string; b: string; r: number; }
/** Reading redundancy: pairs of readings whose values move almost together over history
 * — "one signal wearing two hats" (D-097). Complements leave-one-out. */
export function readingCorrelations(setup: Setup, seriesByTf: Map<string, OHLCPoint[]>, minAbs = 0.85): Redundancy[] {
  if (setup.cols.length < 2) return [];
  const evalTf = evalTimeframe(setup.cols);
  const evalSeries = seriesByTf.get(evalTf) ?? [];
  if (evalSeries.length < 12) return [];
  const colSeries = setup.cols.map((c) => seriesByTf.get(c.tf) ?? []);
  const ptr = setup.cols.map(() => -1);
  const vals: Array<Array<number | null>> = setup.cols.map(() => []);
  for (let i = 0; i < evalSeries.length; i++) {
    const t = evalSeries[i]!.date.getTime();
    for (let c = 0; c < setup.cols.length; c++) {
      const col = setup.cols[c]!;
      let slice: OHLCPoint[];
      if (col.tf === evalTf) slice = evalSeries.slice(0, i + 1);
      else { const s = colSeries[c]!; while (ptr[c]! + 1 < s.length && s[ptr[c]! + 1]!.date.getTime() <= t) ptr[c]!++; slice = ptr[c]! >= 0 ? s.slice(0, ptr[c]! + 1) : []; }
      vals[c]!.push(evalReading(col.reading, slice, { period: col.period }).v);
    }
  }
  const labels = setup.cols.map((c) => SHORT[c.reading] + " " + c.tf);
  const out: Redundancy[] = [];
  for (let i = 0; i < setup.cols.length; i++) {
    for (let j = i + 1; j < setup.cols.length; j++) {
      const xs: number[] = [], ys: number[] = [];
      for (let k = 0; k < vals[i]!.length; k++) { const x = vals[i]![k], y = vals[j]![k]; if (x != null && y != null) { xs.push(x); ys.push(y); } }
      const r = pearson(xs, ys);
      if (r != null && Math.abs(r) >= minAbs) out.push({ a: labels[i]!, b: labels[j]!, r });
    }
  }
  return out.sort((x, y) => Math.abs(y.r) - Math.abs(x.r));
}
